// Header.jsx - Two-tier header with announcement bar and navigation
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, Menu } from 'lucide-react';
import MobileMenu from './MobileMenu';
import './Header.css';

const Header = ({
  cart = [],
  onCartClick
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Calculate total items in cart
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Navigation links
  const navLinks = [
    { path: '/?section=shop', label: 'Shop' },
    { path: '/occasions', label: 'Occasions' },
    { path: '/custom-orders', label: 'Personalised Gifts' },
    { path: '/corporate', label: 'Corporate & Bulk' },
    { path: '/our-work', label: 'Our Work' },
    { path: '/about', label: 'About' }
  ];

  // Handle scroll for sticky header effect
  const handleScroll = useCallback(() => {
    const threshold = window.innerWidth < 768 ? 80 : 100;
    setScrolled(window.scrollY > threshold);
  }, []);

  useEffect(() => {
    // Throttled scroll handler
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });
    handleScroll(); // Check initial state

    return () => window.removeEventListener('scroll', throttledScroll);
  }, [handleScroll]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  // Handle search click - navigate to search page
  const handleSearchClick = () => {
    navigate('/search');
  };

  // Handle shop navigation - scroll to products if on home page
  const handleShopClick = (e) => {
    if (location.pathname === '/') {
      e.preventDefault();
      const productsSection = document.querySelector('.filters-section');
      if (productsSection) {
        productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  // Check if link is active
  const isActiveLink = (path) => {
    if (path === '/') {
      return location.pathname === '/' && !location.search.includes('section=shop');
    }
    if (path === '/?section=shop') {
      return location.pathname === '/' && location.search.includes('section=shop');
    }
    return location.pathname === path;
  };

  return (
    <>
      <header className={`site-header ${scrolled ? 'scrolled' : ''}`}>
        {/* Announcement Bar */}
        <div className="announcement-bar">
          <div className="announcement-content">
            <span className="announcement-text">
              Made personally for you · Crafted in South Africa
            </span>
          </div>
        </div>

        {/* Main Header */}
        <div className="main-header">
          <div className="header-container">
            {/* Logo Section */}
            <Link to="/" className="header-logo-link">
              <div className="header-logo-section">
                <img src="/brand-monogram.svg" alt="" aria-hidden="true" className="header-logo-img" width="58" height="44" />
                <div className="header-brand">
                  <span className="header-brand-name">Two Gs Crafting</span>
                  <span className="header-tagline">WHERE HEART MEETS ART</span>
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="desktop-nav" aria-label="Main navigation">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link ${isActiveLink(link.path) ? 'active' : ''}`}
                  onClick={link.path === '/?section=shop' ? handleShopClick : undefined}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Header Actions */}
            <div className="header-actions">
              {/* Search Icon Button */}
              <button
                className={`icon-button search-toggle ${location.pathname === '/search' ? 'active' : ''}`}
                onClick={handleSearchClick}
                aria-label="Search products"
              >
                <Search size={20} />
              </button>

              {/* Cart Button */}
              <button
                className="icon-button cart-button"
                onClick={onCartClick}
                aria-label={`Cart with ${totalItems} items`}
              >
                <ShoppingCart size={20} />
                {totalItems > 0 && <span className="cart-badge">{totalItems}</span>}
              </button>

              {/* Mobile Menu Toggle */}
              <button
                className="icon-button mobile-menu-toggle"
                onClick={() => setIsMobileMenuOpen(true)}
                aria-label="Open menu"
              >
                <Menu size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        navLinks={navLinks}
        currentPath={location.pathname}
        onShopClick={handleShopClick}
      />
    </>
  );
};

export default Header;
